import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { getRounds, reassignDealDocument } from '../../../services/queries/DealDocQueries';
import {
  invalidateDataState,
  selectedActionState,
  selectedCompanyIdState,
  selectedDocumentState,
} from './updateDealDocsState';

export function ReassignDocumentModal() {
  const [selectedDocument, setSelectedDocument] = useAtom(selectedDocumentState);
  const [selectedAction, setSelectedAction] = useAtom(selectedActionState);
  const [selectedCompanyId] = useAtom(selectedCompanyIdState);
  const setInvalidate = useSetAtom(invalidateDataState);

  const [moveType, setMoveType] = useState<'round' | 'company'>('round');
  const [targetRoundId, setTargetRoundId] = useState<number | null>(null);
  const [rounds, setRounds] = useState<Array<{ id: number; name: string; roundDate: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingRounds, setLoadingRounds] = useState(false);

  const isOpen = selectedAction === 'reassign' && selectedDocument !== null;

  const handleClose = useCallback(() => {
    setSelectedAction(null);
    setSelectedDocument(null);
    setError(null);
    setMoveType('round');
    setTargetRoundId(null);
  }, [setSelectedAction, setSelectedDocument]);

  useEffect(() => {
    if (isOpen && selectedCompanyId) {
      setLoadingRounds(true);
      getRounds(selectedCompanyId)
        .then((roundsData) => {
          setRounds(
            roundsData.map((r: { id: number; name: string; roundDate: string | null }) => ({
              id: r.id,
              name: r.name,
              roundDate: r.roundDate,
            }))
          );
          setError(null);
        })
        .catch((err) => {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load rounds';
          setError(errorMessage);
        })
        .finally(() => {
          setLoadingRounds(false);
        });
    }
  }, [isOpen, selectedCompanyId]);

  const handleSubmit = async () => {
    if (!selectedDocument) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        targetRoundId: moveType === 'round' ? targetRoundId : null,
        targetCompanyId: moveType === 'company' ? selectedCompanyId : null,
      };

      await reassignDealDocument(selectedDocument.id, payload);
      setInvalidate((prev) => prev + 1);
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reassign document';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedDocument) {
    return null;
  }

  const currentLocation = selectedDocument.roundId
    ? `Round (ID: ${selectedDocument.roundId})`
    : 'Company-Level';

  const canSubmit = moveType === 'company' || (moveType === 'round' && targetRoundId !== null);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Update Round for Deal Document</DialogTitle>
      <DialogContent>
        <Stack spacing={3} pt={1}>
          {/* Current Document Info */}
          <Box>
            <Typography variant='subtitle2' color='text.secondary'>
              File Name
            </Typography>
            <Typography>{selectedDocument.fileName}</Typography>
          </Box>

          <Box>
            <Typography variant='subtitle2' color='text.secondary'>
              Current Location
            </Typography>
            <Typography>{currentLocation}</Typography>
          </Box>

          {/* Move Options */}
          <FormControl>
            <Typography variant='subtitle2' mb={1}>
              Move To
            </Typography>
            <RadioGroup value={moveType} onChange={(e) => setMoveType(e.target.value as 'round' | 'company')}>
              <FormControlLabel value='round' control={<Radio />} label='Move to Round' />
              <FormControlLabel value='company' control={<Radio />} label='Move to Company-Level' />
            </RadioGroup>
          </FormControl>

          {/* Round Selector (only if moving to round) */}
          {moveType === 'round' && (
            <FormControl fullWidth>
              <Typography variant='subtitle2' mb={1}>
                Select Target Round
              </Typography>
              <Select
                value={targetRoundId || ''}
                onChange={(e) => setTargetRoundId(Number(e.target.value))}
                displayEmpty
                disabled={loadingRounds}
              >
                <MenuItem value='' disabled>
                  {loadingRounds ? 'Loading rounds...' : 'Choose a round'}
                </MenuItem>
                {rounds
                  .filter((r) => r.id !== selectedDocument.roundId)
                  .map((round) => (
                    <MenuItem key={round.id} value={round.id}>
                      {round.name}
                      {round.roundDate && ` - ${new Date(round.roundDate).toLocaleDateString()}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity='error' onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant='contained' disabled={loading || !canSubmit}>
          {loading ? 'Moving...' : 'Move Document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
